
import Vue, { PropType } from 'vue';
import { CvTextInput, CvMultiSelect } from '@carbon/vue';
import { ArtworkResultItem, CustomerTag, TagOption } from '../typings';

export default Vue.extend({
  components: {
    CvTextInput,
    CvMultiSelect,
  },
  model: {
    prop: 'artwork',
    event: 'update:artwork',
  },
  props: {
    artwork: {
      type: Object as PropType<ArtworkResultItem>,
      required: true,
    },
    tagOptions: {
      type: [] as PropType<string[]>,
      required: true,
    },
  },
  data() {
    return {
      initialTitle: this.artwork.title,
      initialTags: [] as CustomerTag[],
      selectedTags: [] as string[],
      selectOptions: [] as TagOption[],
      initialSelectedTags: [] as CustomerTag[],
      multiSelectTags: [] as string[],
      multiSelectFilterValue: '',
      newTag: [] as TagOption[],
    };
  },
  computed: {
    isDirty(): { title: boolean; tags: boolean } {
      return {
        title: this.artwork.title.trim() !== this.initialTitle.trim(),
        tags: !this.tagsHaveSameValues,
      };
    },
    multiSelectOptionsFilteredBy(): TagOption[] {
      const optionsToFilter = [...this.selectOptions];
      if (this.multiSelectFilterValue === '') {
        return optionsToFilter;
      }
      const filteredOptions = optionsToFilter.filter((tag) =>
        tag.name
          .toLowerCase()
          .includes(this.multiSelectFilterValue.toLowerCase())
      );
      if (filteredOptions.length > 0) {
        return filteredOptions;
      }

      return [...this.selectOptions, ...this.newTag];
    },
    // DO NOT TOUCH WITHOUT CONSULTING, IF YOU CHANGE THIS I WILL KNOW!
    tagsHaveSameValues(): boolean {
      const isTagRemovedOrAdded =
        this.artwork.tags.length != this.initialTags.length;

      if (isTagRemovedOrAdded) {
        return false;
      } else {
        return this.artwork.tags.every((tag) => {
          return this.initialTags
            ?.map((initialTag) => initialTag.name)
            .includes(tag.name);
        });
      }
    },
    isValid(): { title: boolean } {
      return { title: this.artwork.title?.length <= 255 };
    },
  },
  watch: {
    artwork(
      newState: ArtworkResultItem,
      oldState: ArtworkResultItem | undefined
    ) {
      // Checking if the artwork from the oldState is different from the artwork from the newState
      // If so, this means that the initially selected tags have to be changed to the ones from the artwork coming from the newState
      if (!oldState?.id || oldState.id !== newState.id) {
        oldState = {} as ArtworkResultItem;

        this.initialSelectedTags = newState.tags;
        this.initialTags = [...newState.tags];
        this.initialTitle = newState.title;
      }

      // Checking if the artwork from the oldState and newState is the same
      // If so, this means that the initially selected tags have to be the old tags for that same artwork, so we assign the oldState tags
      // If there are tags in the newState then that means that a tag has either been added or removed
      if (newState.id === oldState.id && newState.tags.length > 0) {
        this.initialSelectedTags = oldState?.tags as CustomerTag[];
      }

      // Checking if there are duplicates of tags in the newState and the selectOptions
      let result = this.selectOptions.filter((o1) =>
        newState.tags.some((o2) => o1.name === o2.name)
      );

      // If there are no duplicates in selectOptions and newState, then spread both arrays and create as one
      if (result.length === 0) {
        this.selectOptions = [
          ...this.selectOptions,
          ...newState.tags.map((tag) => ({
            label: tag.name,
            value: tag.name,
            name: tag.name,
          })),
        ];
      }

      // If there are duplicates in selectOptions and newState, then filter selectOptions and create a new array which does not have duplicates
      // Creating a new array out of the unique tags from selectOptions
      var joinedNewStateAndSelectOptions = this.selectOptions.filter(
        (tagFromSelectOptions) =>
          !newState.tags.some(
            (tagFromNewState) =>
              tagFromNewState.name === tagFromSelectOptions.name
          )
      );

      // We are merging the unique tags from selectOptions with the tags from newState
      this.selectOptions = [
        ...joinedNewStateAndSelectOptions,
        ...newState.tags.map((tag) => ({
          label: tag.name,
          value: tag.name,
          name: tag.name,
        })),
      ];
      this.multiSelectTags = newState.tags.map((tag) => tag.name);

      // // when artwork was empty
      if (this.initialTitle === undefined) {
        this.initialTitle = this.artwork.title;
      }

      this.$emit('change', { isDirty: this.isDirty, isValid: this.isValid });
    },
  },
  mounted() {
    this.selectOptions = this.tagOptions.map((tagName) => ({
      value: tagName,
      label: tagName,
      name: tagName,
    }));
  },
  methods: {
    updateValue(key: string, value: string | string[]) {
      this.$emit('update:artwork', {
        ...this.artwork,
        [key]: value,
      });
    },
    onFilter(filterValue: string) {
      if (this.newTag.length === 0) {
        this.newTag.push({
          name: filterValue,
          value: filterValue,
          label: `${filterValue} (NEW)`,
        });
      } else {
        this.newTag.pop();
        this.newTag.push({
          name: filterValue,
          value: filterValue,
          label: `${filterValue} (NEW)`,
        });
      }

      this.multiSelectFilterValue = filterValue.trim();
    },
  },
});
